import { alpha } from "@mui/material";
import type { BrandConfig } from "./types";

// Single graphite scale — every graphite gradient/sheen below is derived from it,
// so retuning the brand only means editing these three values.
const GRAPHITE = {
  base: "#0F0F10",
  mid: "#2A2A2B",
  soft: "#5B5A59",
} as const;

// Graphite shine gradient reused by primary buttons, dividers, and accent bars.
const graphiteGradient = `linear-gradient(270deg, ${GRAPHITE.base} 0%, ${GRAPHITE.mid} 25%, ${GRAPHITE.soft} 50%, ${GRAPHITE.mid} 75%, ${GRAPHITE.base} 100%)`;

// Subtle two-point sheen for the points header, derived from the graphite scale.
const pointsHeaderSheen = [
  `radial-gradient(ellipse at 80% 50%, ${alpha(GRAPHITE.soft, 0.12)} 0%, transparent 60%)`,
  `radial-gradient(ellipse at 20% 80%, ${alpha(GRAPHITE.mid, 0.1)} 0%, transparent 70%)`,
].join(", ");

export const stageBrand: BrandConfig = {
  name: "stage",
  siteTitle: "STAGE Member",

  // Primitive color tokens reused across multiple features.
  colors: {
    // Global neutrals and transparent keyword.
    common: {
      white: "#FFFFFF",
      black: "#000000",
      transparent: "transparent",
    },

    // Brand identity, gradients, and accent fills (mono graphite on white).
    brand: {
      secondary: "#5B5A59",
      gradientStart: "#0F0F10",
      gradientMiddle: "#2A2A2B",
      gradientAccent: "#5B5A59",
      gradientWarm: "#2A2A2B",
      gradientEnd: "#0F0F10",
      accentBronze: "#5B5A59",
      accentCritical: "#0F0F10",
      accentCriticalStrong: "#0F0F10",
      pointsAccent: "#FFFFFF",
      pointsIcon: "#0F0F10",
      pointsGradientStart: "#0F0F10",
      pointsGradientMid: "#2A2A2B",
      pointsGradientEnd: "#5B5A59",
    },

    // Generic background surfaces and feedback fills.
    background: {
      appSecondary: "#F5F5F5",
      pagePrimary: "#FFFFFF",
      surfaceSubtle: "#FAFAFA",
      fieldPrimary: "#F5F5F5",
      feedbackSuccess: "#F5FEE9",
      feedbackWarning: "#FDF5E8",
      feedbackNeutral: "#F1F1F1",
      feedbackFreeze: "#E3F2FD",
    },

    // Generic text primitives.
    text: {
      onDarkPlaceholder: "#797677",
      onLightSecondary: "rgba(0, 0, 0, 0.6)",
      primary: "#0F0F10",
      primaryAlt: "#0F0F10",
      primaryStrong: "#0F0F10",
      tertiary: "#8E8A86",
      tertiaryAlt: "#8E8E93",
      disabled: "#C7C6C6",
      emphasis: "rgba(0, 0, 0, 0.87)",
      disabledOnLight: "rgba(0, 0, 0, 0.38)",
    },

    // Shared border and outline colors.
    border: {
      secondary: "#E5E1E1",
      divider: "#E5E1E1",
      strong: "#5B5A59",
      muted: "#C6C6C6",
      neutral: "#BDBDBD",
      iconSurface: "#E0E0E0",
    },

    // Semantic status colors.
    state: {
      error: "#BA1A1A",
      success: "#2E7D32",
      successBright: "#33B15B",
      warning: "#F18230",
      warningAlt: "#ED6C02",
      neutral: "#9E9E9E",
      freeze: "#2AACE2",
    },

    // Overlay colors for disabled, video, and translucent UI states.
    overlay: {
      disabled: "rgba(0, 0, 0, 0.12)",
      video: "rgba(0,0,0,0.35)",
      videoEnd: "rgba(0,0,0,0.45)",
      onDarkSoft: "rgba(255, 255, 255, 0.1)",
      onDarkDisabled: "rgba(255, 255, 255, 0.16)",
      mutedLight: "rgba(198, 198, 198, 0.12)",
    },

    // Shared icon fills.
    icon: {
      badgeBackground: "#F5F5F5",
      badgeForeground: "#2A2A2B",
      poolForeground: "#0F0F10",
      targetForeground: "#C7C6C6",
    },
  },

  // Non-trivial gradient strings.
  gradients: {
    videoOverlay:
      "linear-gradient(180deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.45) 100%)",
  },

  // Authentication page composition.
  auth: {
    loginHeader: {
      title: "Login",
      showDivider: true,
    },
    footer: {
      logo: "fitup",
      text: "Powered by FitUp",
    },
  },

  // Member area screen-specific tokens.
  member: {
    secondaryCardBackground: "#F5F5F5",
    headerBackground: "#0F0F10",
    headerPrimaryText: "#FFFFFF",
    headerSecondaryText: "rgba(255, 255, 255, 0.6)",
    sectionTitleColor: "#0F0F10",
    sectionDividerColor: "#E5E1E1",
    emptyStateText: "#5B5A59",
    pointsPage: {
      headerBackground: "#FFFFFF",
      headerBackgroundGradient: pointsHeaderSheen,
      headerText: "#0F0F10",
      filterIcon: "#797677",
      monthText: "#0F0F10",
      transactionDate: "#797677",
      transactionDivider: "#E5E1E1",
    },
    qrSection: {
      cardBackground: "#F5F5F5",
      iconBackground: "#0F0F10",
      iconColor: "#FFFFFF",
      labelColor: "#5B5A59",
    },
    goalSetting: {
      dateActiveBackground: "#2A2A2B",
      dateActiveBorder: "#2A2A2B",
      dateActiveText: "#FFFFFF",
      dateInactiveBackground: "#F5F5F5",
      dateInactiveBorder: "#E5E1E1",
      dateInactiveText: "#797677",
      cardBackground: "#F5F5F5",
      cardTitle: "#0F0F10",
      cardText: "#0F0F10",
      cardMutedText: "#797677",
      cardDivider: "#E5E1E1",
      cardActionText: "#5B5A59",
      iconBackground: "#F5F5F5",
      iconForeground: "#0F0F10",
      targetIcon: "#797677",
      chartLine: "#C7C6C6",
      chartLabel: "#C7C6C6",
      chartGrid: "#E5E1E1",
      sectionAccent: "#0F0F10",
    },
  },

  booking: {
    timeSlot: {
      titleColor: "#0F0F10",
      borderColor: "#E5E1E1",
      textColor: "#797677",
      selectedBackground: "#0F0F10",
      selectedTextColor: "#FFFFFF",
      hoverBackground: "rgba(198, 198, 198, 0.12)",
    },
  },

  facility: {
    booking: {
      paymentSlip: {
        titleColor: "#0F0F10",
      },
    },
  },

  // Consumer-facing palette consumed by COLORS, theme, and feature styles.
  palette: {
    // Core brand and shell tokens.
    primary: "#0F0F10",
    primaryLight: "#F5F5F5",
    bodyBackground: "#FFFFFF",
    gradient: graphiteGradient,
    loginBackground:
      "linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 50%, #F5F5F5 100%)",

    // Card surfaces and on-card text.
    cardPrimary: "#F5F5F5",
    cardSurface: "#FAFAFA",
    upcomingChipBackground: "#E5E1E1",
    cardText: "#5B5A59",
    cardTitle: "#0F0F10",
    cardTextMuted: "#797677",
    cardTextDim: "#8E8A86",
    cardDivider: "#E5E1E1",

    // Page-level typography.
    textPrimary: "#0F0F10",
    scheduleSectionMonth: "#0F0F10",
    textSecondary: "#5B5A59",
    readMoreButtonColor: "#5B5A59",

    // Shared accents and filters.
    accentActive: "#0F0F10",

    filterActiveBg: "#0F0F10",
    datePickerBackground: "#F5F5F5",
    selectOptionBackground: "#F5F5F5",
    filterActiveColor: "#FFFFFF",
    filterInactiveBg: "transparent",
    filterInactiveColor: "#797677",
    filterInactiveBorder: "1px solid #E5E1E1",
    filterActiveBorder: "none",
    trainerFilterActiveBg: "#2A2A2B",
    trainerFilterActiveColor: "#FFFFFF",
    trainerFilterActiveHoverBg: "#2A2A2B",
    trainerFilterActiveBorder: "none",
    trainerFilterInactiveBg: "transparent",
    trainerFilterInactiveColor: "#797677",
    trainerFilterInactiveHoverBg: "transparent",
    trainerFilterInactiveBorder: "1px solid #E5E1E1",

    // Stepper state colors.
    stepperActive: "#0F0F10",
    stepperInactive: "#C7C6C6",

    // Page-muted text and brand logo fill.
    pageTextMuted: "#797677",
    logoFill: "#0F0F10",

    // Tabs and date buttons.
    tabInactiveColor: "#797677",
    tabActiveColor: "#0F0F10",
    dateButtonSelectedText: "#FFFFFF",

    // Empty-state and sheet/dialog tokens.
    emptyStateIconColor: "#C7C6C6",

    sheetBackground: "#FFFFFF",
    sheetText: "#0F0F10",
    reservationDateRangeIcon: "#797677",
    dialogText: "#0F0F10",
    dialogTextMuted: "#797677",
    dialogPointIcon: "#0F0F10",
    fabBackground: "#FFFFFF",
    fabBackgroundHover: "#FFFFFF",
    errorLight: "#C74C3F",
  },

  // Shared shadows.
  shadows: {
    card: "0px 1px 3px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.3)",
    bottomSheet: "0px -12px 40px rgba(0, 0, 0, 0.5)",
    cardSubtle: "0px 1px 2px rgba(0, 0, 0, 0.04)",
    button: "0px 4px 12px rgba(0,0,0,0.25)",
    buttonHover: "0px 6px 16px rgba(0,0,0,0.3)",
    dialog: "0px 12px 32px rgba(0, 0, 0, 0.18)",
    dialogHover: "0px 6px 14px rgba(0, 0, 0, 0.25)",
    videoPlay: "0 8px 16px rgba(0,0,0,0.35)",
  },

  // Shared button system tokens.
  button: {
    surface: "gradient",
    backgroundColor: "#0F0F10",
    color: "#FFFFFF",
    hoverBackgroundColor: "#2A2A2B",
    hoverColor: "#FFFFFF",
    disabled: {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      color: "rgba(0, 0, 0, 0.38)",
    },
    secondary: {
      backgroundColor: "#FFFFFF",
      color: "#0F0F10",
      borderColor: "#BDBDBD",
      hoverBackgroundColor: "#FFFFFF",
      hoverColor: "#0F0F10",
      hoverBorderColor: "#BDBDBD",
    },
  },
};
